// components/UnifiedAvatarCard.tsx
import React, { useRef, useEffect } from "react";
import { CircularProgress } from "@mui/material";
import { AvatarGroup, CustomPhotoAvatar } from "../../../../context/types";
import CheckMark from "../CheckMark";
import { THEME_COLORS } from "../../../../context/constants";
import ThreeDotMenu from "../ThreeDotMenu";
import GroupThreeDotMenu from "../GroupThreeDotMenu";
import LockIcon from "../LockIcon";

interface UnifiedAvatarCardProps {
  item: AvatarGroup | CustomPhotoAvatar;
  type: "group" | "avatar";
  isSelected?: boolean;
  canSelect?: boolean;
  canCreateGroup?: boolean;
  isGroupCreating?: boolean; // Avatar's group is being created/trained
  selectedAvatarLabActors?: CustomPhotoAvatar[];
  onSelectAvatarLabActor?: (avatars: CustomPhotoAvatar) => void;
  onOpenGroupCreation?: (avatar: CustomPhotoAvatar) => void;
  onOpenGroupLookCreation: (group: AvatarGroup) => void;
  onOpenChooseLookModal: (group: AvatarGroup) => void;
  onDeleteAvatar?: (avatarId: string) => void;
  onDeleteGroup?: (groupId: string) => void;
  // Avatar Lab: open the group-level "set default voice" dialog.
  onSetGroupDefaultVoice?: (group: AvatarGroup) => void;
  onRefreshGroup: (groupId: string) => void;
  isCreatingGroup?: boolean;
  hoveredActorId?: string | null;
  onMouseEnter?: (id: string) => void;
  onMouseLeave?: () => void;
  classes: any; // Pass the styles object
  // Motion and sound effect props
  onOpenAddMotion?: (avatar: CustomPhotoAvatar) => void;
  onAddSoundEffect?: (avatar: CustomPhotoAvatar) => void;
  isAddingMotion?: boolean;
  isAddingSoundEffect?: boolean;
  // V3: rename a look (only present for non-prompt V3 avatars)
  onRenameLook?: (newName: string) => void;
}

const UnifiedAvatarCard: React.FC<UnifiedAvatarCardProps> = ({
  item,
  type,
  isSelected = false,
  canSelect = true,
  canCreateGroup = false,
  isGroupCreating = false,
  selectedAvatarLabActors = [],
  onSelectAvatarLabActor,
  onOpenGroupCreation,
  onOpenGroupLookCreation,
  onOpenChooseLookModal,
  onDeleteAvatar,
  onDeleteGroup,
  onSetGroupDefaultVoice,
  onRefreshGroup,
  isCreatingGroup = false,
  hoveredActorId,
  onMouseEnter,
  onMouseLeave,
  classes,
  onOpenAddMotion,
  onAddSoundEffect,
  isAddingMotion = false,
  isAddingSoundEffect = false,
  onRenameLook,
}) => {
  const isGroup = type === "group";
  const group = item as AvatarGroup;
  const avatar = item as CustomPhotoAvatar;

  const itemId = isGroup ? group._id : avatar._id;
  const isHovered = hoveredActorId === itemId;

  // Video ref for motion preview
  const videoRef = useRef<HTMLVideoElement>(null);

  // Get motion preview URL (for standalone avatars or groups with motion)
  const getMotionPreviewUrl = () => {
    if (isGroup) {
      return group.motion_preview_url;
    }
    return avatar.motion_preview_url;
  };

  const hasMotion = isGroup ? group.is_motion : avatar.is_motion;
  const motionPreviewUrl = getMotionPreviewUrl();

  // Handle video playback on hover
  useEffect(() => {
    if (videoRef.current && hasMotion && motionPreviewUrl) {
      if (isHovered) {
        videoRef.current.play().catch((error) => {
          console.error("Motion preview play failed:", error);
        });
      } else {
        videoRef.current.pause();
        videoRef.current.currentTime = 0;
      }
    }
  }, [isHovered, hasMotion, motionPreviewUrl]);

  const handleCardClick = () => {
    if (isGroup && canSelect) {
      // For groups, select/deselect the group
      const selectedLooks = selectedAvatarLabActors.filter(
        (selected) => selected.avatar_group === group._id
      );
      if (selectedLooks.length > 0) {
        // Deselect all looks from this group
        selectedLooks.forEach((look) => {
          onSelectAvatarLabActor?.(look);
        });
      } else {
        // Select the base avatar (first look)
        const baseLook = group.looks?.[0];
        if (baseLook && typeof baseLook === "object") {
          onSelectAvatarLabActor?.(baseLook);
        }
      }
    } else if (canSelect && !isGroup) {
      // For standalone avatars, select them
      onSelectAvatarLabActor?.(avatar);
    }
  };

  const handleMouseEnter = () => {
    onMouseEnter?.(itemId);
  };

  const getImageUrl = () => {
    if (isGroup) {
      // First try group.image_url, then fallback to first look's image
      if (group.image_url) {
        return group.image_url;
      }
      // Check if first look has an image
      const firstLook = group.looks?.[0];
      if (firstLook && typeof firstLook === "object" && firstLook.image_url_list?.length > 0) {
        return firstLook.image_url_list[0];
      }
      return "/placeholder-avatar.png";
    } else {
      // V2 selected images live in image_url_list (populated by the V2 status
      // callback after the user picks one). V3 avatars set preview_image_url
      // at creation time and may never populate image_url_list — so we fall
      // back to preview_image_url before the placeholder.
      if (
        avatar.status === "success" &&
        avatar.image_url_list &&
        avatar.image_url_list.length > 0
      ) {
        return avatar.image_url_list[0];
      }
      if (avatar.preview_image_url) return avatar.preview_image_url;
      return "/placeholder-avatar.png";
    }
  };

  const getName = () => {
    return isGroup ? group.name : avatar.name;
  };

  const getGender = () => {
    return isGroup ? "" : avatar.gender;
  };

  const getLooksCount = () => {
    if (!isGroup) return 0;
    return group.looks?.length || 0;
  };

  const getSelectedLooksCount = () => {
    if (!isGroup) return 0;
    return selectedAvatarLabActors.filter(
      (look) => look.avatar_group === group._id
    ).length;
  };

  const getStatus = () => {
    if (isGroup) {
      return group.training_status;
    } else {
      return avatar.status;
    }
  };

  const getLookImage = (index: number) => {
    if (!isGroup || !group.looks || group.looks.length <= index) return null;
    const look = group.looks[index];
    if (
      typeof look === "object" &&
      look.image_url_list &&
      look.image_url_list.length > 0
    ) {
      return look.image_url_list[0];
    }
    return null;
  };

  const selectedLooksCount = getSelectedLooksCount();
  const looksCount = getLooksCount();

  return (
    <div
      className={`${classes.actorCard} ${isSelected ? classes.selected : ""} ${
        !canSelect ? classes.locked : ""
      }`}
      onClick={handleCardClick}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={onMouseLeave}
      style={{
        position: "relative",
        cursor: canSelect ? "pointer" : "not-allowed",
      }}
    >
      {/* Playing Cards Effect for Groups */}
      {isGroup && looksCount > 0 && (
        <>
          {/* Layer 3 (farthest back) - Third look or yellow background */}
          <div
            style={{
              position: "absolute",
              top: "8px",
              left: "8px",
              right: "-4px",
              bottom: "-8px",
              backgroundColor:
                looksCount >= 3 && getLookImage(2)
                  ? "white"
                  : THEME_COLORS.darkYellow || "#FFA000",
              borderRadius: "10px",
              zIndex: 1,
              transform: "rotate(-5deg)",
              overflow: "hidden",
              border: "1px solid rgba(0,0,0,0.1)",
            }}
          >
            {looksCount >= 3 && getLookImage(2) && (
              <img
                src={getLookImage(2) || ""}
                alt="Third look"
                style={{
                  width: "100%",
                  height: "100%",
                  objectFit: "cover",
                }}
              />
            )}
          </div>

          {/* Layer 2 (middle) - Second look */}
          {looksCount >= 2 && getLookImage(1) && (
            <div
              style={{
                position: "absolute",
                top: "4px",
                left: "4px",
                right: "-2px",
                bottom: "-4px",
                backgroundColor: "white",
                borderRadius: "10px",
                zIndex: 2,
                transform: "rotate(-3deg)",
                overflow: "hidden",
                border: "1px solid rgba(0,0,0,0.1)",
              }}
            >
              <img
                src={getLookImage(1) || ""}
                alt="Second look"
                style={{
                  width: "100%",
                  height: "100%",
                  objectFit: "cover",
                }}
              />
            </div>
          )}

          {/* Layer 1 (closest to main) - First look (base avatar) */}
          <div
            style={{
              position: "absolute",
              top: "2px",
              left: "2px",
              right: "-1px",
              bottom: "-2px",
              backgroundColor: "white",
              borderRadius: "10px",
              zIndex: 3,
              transform: "rotate(4deg)",
              overflow: "hidden",
              border: "1px solid rgba(0,0,0,0.1)",
            }}
          >
            <img
              src={getLookImage(0) || "/placeholder-avatar.png"}
              alt="First look"
              style={{
                width: "100%",
                height: "100%",
                objectFit: "cover",
              }}
            />
          </div>
        </>
      )}

      {/* Three Dot Menu */}
      <div style={{ position: "absolute", top: 4, right: 4, zIndex: 10 }}>
        {isGroup ? (
          <GroupThreeDotMenu
            group={group}
            onOpenLookCreation={onOpenGroupLookCreation}
            onOpenChooseLook={onOpenChooseLookModal}
            onRefreshGroup={onRefreshGroup}
            onDeleteGroup={(onDeleteGroup ?? onDeleteAvatar)!}
            onSetDefaultVoice={onSetGroupDefaultVoice}
            selectedAvatarLabActors={selectedAvatarLabActors}
          />
        ) : (
          // !isSelected && (
          <ThreeDotMenu
            avatar={avatar}
            onDelete={onDeleteAvatar!}
            onOpenGroupCreation={onOpenGroupCreation!}
            canCreateGroup={canCreateGroup}
            isCreatingGroup={isCreatingGroup}
            onOpenAddMotion={onOpenAddMotion}
            onAddSoundEffect={onAddSoundEffect}
            isAddingMotion={isAddingMotion}
            isAddingSoundEffect={isAddingSoundEffect}
            onRename={
              onRenameLook
                ? () => {
                    const newName = window.prompt("Rename avatar look", avatar.name);
                    if (newName && newName.trim()) onRenameLook(newName.trim());
                  }
                : undefined
            }
          />
          //)
        )}
      </div>

      {/* Status Indicator */}
      <div
        style={{
          position: "absolute",
          bottom: "8px",
          right: "4px",
          zIndex: 6,
          padding: "4px 8px",
          borderRadius: "12px",
          fontSize: "10px",
          fontWeight: "bold",
          backgroundColor:
            getStatus() === "ready" || getStatus() === "success"
              ? "#4CAF50"
              : getStatus() === "training" || getStatus() === "processing"
              ? "#FFA500"
              : getStatus() === "failed"
              ? "#F44336"
              : "#9E9E9E",
          color: "white",
        }}
      >
        {getStatus().charAt(0).toUpperCase() + getStatus().slice(1)}
      </div>

      {/* Avatar Lab Badge */}
      <div
        style={{
          position: "absolute",
          top: "8px",
          left: "8px",
          zIndex: 6,
          padding: "2px 6px",
          borderRadius: "8px",
          fontSize: "10px",
          fontWeight: "bold",
          backgroundColor: "#9C27B0",
          color: "white",
        }}
      >
        {isGroup ? "Looks Group" : "Avatar Lab"}
      </div>

      {/* Looks Badge for Groups - SILVER */}
      {isGroup && (
        <div
          style={{
            position: "absolute",
            top: "10px",
            left: "8px",
            zIndex: 6,
            padding: "2px 6px",
            borderRadius: "8px",
            fontSize: "10px",
            fontWeight: "bold",
            backgroundColor: "#C0C0C0", // Silver color
            color: "white",
            marginTop: "20px", // Below the Avatar Lab badge
          }}
        >
          {looksCount} Looks
        </div>
      )}

      <div
        className={classes.actorInfo}
        style={{ position: "absolute", zIndex: 6 }}
      >
        <span
          style={{
            marginBottom: "8px",
            fontWeight: "bold",
          }}
        >
          {getName().length > 10
            ? getName().substring(0, 10) + "..."
            : getName()}
        </span>
        {!isGroup && <span>{getGender()}</span>}
      </div>

      {/* Motion Badge */}
      {hasMotion && (
        <div
          style={{
            position: "absolute",
            bottom: "32px",
            left: "8px",
            zIndex: 7,
            padding: "2px 6px",
            borderRadius: "8px",
            fontSize: "9px",
            fontWeight: "bold",
            backgroundColor: "#9C27B0",
            color: "white",
          }}
        >
          Motion
        </div>
      )}

      {/* Motion Preview Video */}
      {hasMotion && motionPreviewUrl && (
        <video
          ref={videoRef}
          src={motionPreviewUrl}
          loop
          muted
          playsInline
          preload="metadata"
          style={{
            position: "absolute",
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
            zIndex: 5,
            opacity: isHovered ? 1 : 0,
            transition: "opacity 0.3s ease-in-out",
            objectFit: "cover",
            borderRadius: "10px",
          }}
        />
      )}

      {/* Main image (the actual card content) */}
      <img
        src={getImageUrl()}
        alt={getName()}
        className={classes.actorImage}
        style={{
          zIndex: 4, // Above background layers but below overlays
          minWidth: "150px",
          minHeight: "150px",
          width: "100%",
          height: "100%",
          backgroundColor: THEME_COLORS.gray,
          filter:
            getStatus() === "processing" || getStatus() === "training"
              ? "grayscale(50%)"
              : "none",
        }}
      />

      {/* Selected State - Yellow Overlay with Check Mark */}
      {isSelected && (
        <>
          <div
            style={{
              position: "absolute",
              top: 0,
              left: 0,
              width: "100%",
              height: "100%",
              backgroundColor: "rgba(255, 191, 0, 0.3)",
              borderRadius: "10px",
              zIndex: 5,
            }}
          />
          <CheckMark isVisible={true} />

          {/* Selected Looks Count for Groups */}
          {isGroup && selectedLooksCount > 0 && (
            <div
              style={{
                position: "absolute",
                top: "50%",
                left: "50%",
                transform: "translate(-50%, -50%)",
                zIndex: 6,
                backgroundColor: "rgba(255, 191, 0, 0.9)",
                color: "white",
                padding: "8px 12px",
                borderRadius: "20px",
                fontSize: "14px",
                fontWeight: "bold",
                textAlign: "center",
              }}
            >
              {selectedLooksCount} look{selectedLooksCount !== 1 ? "s" : ""}{" "}
              selected
            </div>
          )}
        </>
      )}

      {/* Processing/Training/Pending Overlay */}
      {/* Show only ONE loading state - prioritize group creating over avatar processing */}
      {(isGroupCreating || (getStatus() === "processing" || getStatus() === "training" || getStatus() === "pending")) && (
        <div
          style={{
            position: "absolute",
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
            backgroundColor: "rgba(0,0,0,0.7)",
            borderRadius: "10px",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 8,
          }}
        >
          <CircularProgress style={{ color: THEME_COLORS.yellow }} />
          <span
            style={{
              color: "white",
              marginTop: "8px",
              fontSize: "12px",
              textAlign: "center",
              padding: "0 8px",
            }}
          >
            {isGroupCreating
              ? "Setting up avatar..."
              : getStatus() === "training"
              ? "Training..."
              : getStatus() === "pending"
              ? "Pending..."
              : "Processing..."}
          </span>
        </div>
      )}

      {/* Failed Overlay */}
      {getStatus() === "failed" && (
        <div
          style={{
            position: "absolute",
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
            backgroundColor: "rgba(244,67,54,0.8)",
            borderRadius: "10px",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 5,
            padding: "8px",
            textAlign: "center",
          }}
        >
          <span style={{ color: "white", fontSize: "12px" }}>
            Generation Failed
          </span>
        </div>
      )}
    </div>
  );
};

export default UnifiedAvatarCard;
